import { communityDefaultReactions } from '@/components/community/community-reactions-config'
import NextImage from '@/components/NextImage'

import { MessageEmojiData } from '@/types/chat.type'

const DEFAULT_REACTION_TYPES = new Set(['like', 'love', 'haha', 'wow', 'sad', 'angry'])

type ReactionEmojiData = {
  unicode?: string
  imageUrl?: string
  name?: string
}

export function isSameReactionSelection(
  existingReactionType?: string,
  existingEmojiData?: ReactionEmojiData,
  incomingReactionType?: string,
  incomingEmojiData?: ReactionEmojiData
): boolean {
  if (!existingReactionType || !incomingReactionType) return false
  if (existingReactionType !== incomingReactionType) return false

  if (DEFAULT_REACTION_TYPES.has(existingReactionType)) return true

  const existingEmoji = existingEmojiData
  const incomingEmoji = incomingEmojiData
  if (!existingEmoji && !incomingEmoji) return true
  if (!existingEmoji || !incomingEmoji) return false

  if (existingEmoji.imageUrl && incomingEmoji.imageUrl) {
    return existingEmoji.imageUrl === incomingEmoji.imageUrl
  }

  if (existingEmoji.unicode && incomingEmoji.unicode) {
    return existingEmoji.unicode === incomingEmoji.unicode
  }

  return false
}

export function getReactionGroupKey(reaction: {
  reactionType: string
  emojiData?: ReactionEmojiData
}): string {
  if (DEFAULT_REACTION_TYPES.has(reaction.reactionType)) {
    return reaction.reactionType
  }

  if (reaction.emojiData?.imageUrl) {
    return reaction.emojiData.imageUrl
  }

  if (reaction.emojiData?.unicode) {
    const unicode = reaction.emojiData.unicode
    if (unicode.includes('<img')) {
      const imgMatch = unicode.match(/src="([^"]+)"/)
      if (imgMatch) return imgMatch[1]
    }
    if (!isPlainTextLabel(unicode)) {
      return unicode
    }
  }

  return reaction.reactionType
}

export function getDefaultReactionEmoji(reactionType?: string): string | undefined {
  if (!reactionType) return undefined

  return communityDefaultReactions[reactionType]?.emoji
}

function isPlainTextLabel(value: string): boolean {
  return /^[a-zA-Z0-9_\s-]+$/.test(value)
}

export function renderReactionIcon(
  emojiData?: MessageEmojiData,
  reactionType?: string,
  size: 'sm' | 'md' = 'md'
) {
  const sizeConfig = {
    sm: { image: 16, text: 'text-lg' },
    md: { image: 24, text: 'text-2xl' },
  }
  const { image: imageSize, text: textClass } = sizeConfig[size]

  if (emojiData?.imageUrl) {
    return (
      <NextImage
        src={emojiData.imageUrl}
        alt={emojiData.name || 'custom reaction'}
        width={imageSize}
        height={imageSize}
        className="rounded object-cover"
        title={emojiData.name || 'custom reaction'}
        unoptimized
      />
    )
  }

  if (emojiData?.unicode) {
    if (emojiData.unicode.includes('<img')) {
      const imgMatch = emojiData.unicode.match(/src="([^"]+)"/)
      if (imgMatch) {
        return (
          <NextImage
            src={imgMatch[1]}
            alt={emojiData.name || 'custom reaction'}
            width={imageSize}
            height={imageSize}
            className="rounded object-cover"
            title={emojiData.name || 'custom reaction'}
            unoptimized
          />
        )
      }
    }

    if (!isPlainTextLabel(emojiData.unicode)) {
      return (
        <span className={`flex items-center justify-center ${textClass}`} title={emojiData.name || emojiData.unicode}>
          {emojiData.unicode}
        </span>
      )
    }
  }

  const defaultEmoji = getDefaultReactionEmoji(reactionType)
  const displayValue = defaultEmoji ?? reactionType ?? 'reaction'

  return (
    <span className={`flex items-center justify-center ${textClass}`} title={reactionType || 'reaction'}>
      {displayValue}
    </span>
  )
}
